import { useEffect, useState, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { IconPlus } from "@tabler/icons-react";

import { useProductListDataById, useProductAllListData } from "../../../apis/promoApis";
import { useProfileData } from "../../../apis/profileApis";
import ShipmentItemRow from "./ShipmentItemRow";

type ProductItem = {
  pId: string;
  quantity: string;
  weight: number;
};

interface ShipmentItemSectionProps {
  control: any;
  setValue: any;
  watch?: any;
  register: any;
  errors: any;
  isSidebar?: boolean;
}

export default function ShipmentItemSection({
  control,
  setValue,
  register,
  errors,
  isSidebar = false,
}: ShipmentItemSectionProps) {
  const { data: profile } = useProfileData();
  const selectedClientId = useWatch({
    control,
    name: "clientId",
  }) as string | undefined;

  const clientIdToUse = selectedClientId || profile?.id;
  const { data: productsById } = useProductListDataById(clientIdToUse, true);
  const { data: allProducts } = useProductAllListData();

  const products = useMemo(() => {
    return (productsById && Array.isArray(productsById) && productsById.length > 0)
      ? productsById
      : (Array.isArray(allProducts) ? allProducts : []);
  }, [productsById, allProducts]);

  const productsArray = useWatch({
    control,
    name: "products",
  }) as ProductItem[] | undefined;

  const lastRowIndex = (productsArray?.length ?? 1) - 1;
  const lastRowPId = useWatch({
    control,
    name: `products[${lastRowIndex}].pId`,
  }) as string | undefined;

  const lastRowQuantity = useWatch({
    control,
    name: `products[${lastRowIndex}].quantity`,
  }) as string | undefined;

  const [rows, setRows] = useState<{ id: number }[]>([{ id: Date.now() }]);

  useEffect(() => {
    const current = productsArray || [];

    if (current.length === 0) {
      setValue("products", [{ pId: "", quantity: "", weight: 0 }], {
        shouldValidate: true,
      });
      setRows([{ id: Date.now() }]);
      return;
    }

    if (current.length !== rows.length) {
      const newRows = current.map((_, i) => ({
        id: rows[i]?.id ?? Date.now() + i,
      }));
      setRows(newRows);
    }
  }, [productsArray?.length, setValue, rows.length]);

  const totalProductOptions = useMemo(() => {
    if (!products || !Array.isArray(products)) return 0;
    let count = 0;
    products.forEach((p: any) => {
      if (Array.isArray(p?.variants) && p.variants.length > 0) {
        count += p.variants.length;
      } else {
        count += 1;
      }
    });
    return count;
  }, [products]);

  const addRow = () => {
    if (!products || totalProductOptions === 0) return;
    const current = productsArray || [];
    if (current.length >= totalProductOptions) return;

    setRows((prev) => [...prev, { id: Date.now() }]);
    setValue("products", [...current, { pId: "", quantity: "", weight: 0 }], {
      shouldValidate: true,
    });
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    const current = productsArray || [];
    const updated = current.filter((_, i) => i !== index);
    setValue("products", updated, { shouldValidate: true });
  };

  useEffect(() => {
    if (!productsArray || !products) return;

    productsArray.forEach((item, index) => {
      if (!item.pId) return;

      const selectedProduct = products.find((p: any) => p.id === item.pId);
      if (selectedProduct && selectedProduct.weight !== item.weight) {
        setValue(`products[${index}].weight`, selectedProduct.weight, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
  }, [productsArray, products, setValue]);

  useEffect(() => {
    if (!productsArray) return;

    const totals = productsArray.reduce(
      (acc, p) => {
        const qty = Number(p.quantity) || 0;
        const wt = Number(p.weight) || 0;
        acc.qty += qty;
        acc.wt += qty * wt;
        return acc;
      },
      { qty: 0, wt: 0 }
    );

    setValue("shipment.numberOfPieces", totals.qty, { shouldValidate: true });
    setValue("shipment.totalWeight", totals.wt, { shouldValidate: true });
  }, [productsArray, setValue]);

  const isHeaderAddDisabled =
    !clientIdToUse ||
    totalProductOptions === 0 ||
    !lastRowPId ||
    !lastRowQuantity ||
    (productsArray?.length ?? 0) >= totalProductOptions;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center pb-1 border-b border-[#FBD037]/30">
        <p className="text-[#212121]/80 font-bold text-xs uppercase tracking-widest">
          Add Items
        </p>
        <button
          type="button"
          disabled={isHeaderAddDisabled}
          onClick={addRow}
          className={`bg-[#FBD037]/20 border border-[#FBD037] rounded-lg p-1.5 transition-all flex items-center justify-center ${
            isHeaderAddDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#FBD037]/30 cursor-pointer"
          }`}
          title="Add Item"
        >
          <IconPlus size={16} stroke={1.5} className="text-black" />
        </button>
      </div>

      <div className={isSidebar ? "flex flex-col gap-3" : "grid grid-cols-2 gap-4"}>
        {rows.map((row, index) => {
          return (
            <ShipmentItemRow
              key={row.id}
              index={index}
              control={control}
              setValue={setValue}
              register={register}
              errors={errors}
              products={products}
              onRemove={() => removeRow(index)}
              onAdd={addRow}
              isLastRow={index === rows.length - 1}
              lastRowQuantity={lastRowQuantity}
              isSidebar={isSidebar}
            />
          );
        })}
      </div>
    </div>
  );
}